/**
 * EloRatingCard — Displays a user's Elo/Glicko-2 rating with trend.
 */
import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { Lock , TrendingUp, TrendingDown, Minus, Zap } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { getEloRating } from '@/lib/elo';


interface EloRatingCardProps {
  profileId: string;
  category?: string;
  compact?: boolean;
  showCoachInsights?: boolean;
}

function getRankTier(rating: number): { label: string; color: string; emoji: string } {
  if (rating >= 2200) return { label: 'GRANDMASTER', color: '#FFD700', emoji: '👑' };
  if (rating >= 2000) return { label: 'MASTER', color: '#C0392B', emoji: '🔴' };
  if (rating >= 1800) return { label: 'DIAMOND', color: '#3498DB', emoji: '💎' };
  if (rating >= 1600) return { label: 'PLATINUM', color: '#1ABC9C', emoji: '💠' };
  if (rating >= 1400) return { label: 'GOLD', color: '#F39C12', emoji: '🥇' };
  if (rating >= 1200) return { label: 'SILVER', color: '#95A5A6', emoji: '🥈' };
  if (rating >= 1000) return { label: 'BRONZE', color: '#CD7F32', emoji: '🥉' };
  return { label: 'IRON', color: '#7F8C8D', emoji: '⚙️' };
}

export default function EloRatingCard({ profileId, category = 'overall', compact = false, showCoachInsights = false }: EloRatingCardProps) {
  const { theme } = useTheme();
  const router = useRouter();
  const c = theme.colors;
  const { tier } = useSubscription();
  const isPro = hasAtLeast(tier, 'pro');
  const [rating, setRating] = useState<{ rating: number; deviation: number; volatility: number; wins: number; losses: number; draws: number } | null>(null);

  useEffect(() => {
    if (!profileId) return;
    getEloRating(profileId, category).then(setRating).catch(() => {});
  }, [profileId, category]);

  if (!rating) return null;

  const rankTier = getRankTier(rating.rating);
  const totalGames = rating.wins + rating.losses + rating.draws;
  const winRate = totalGames > 0 ? Math.round((rating.wins / totalGames) * 100) : 0;

  if (compact) {
    return (
      <View style={[styles.compactContainer, { borderColor: rankTier.color + '40' }]}>
        <Text style={styles.compactEmoji}>{rankTier.emoji}</Text>
        <Text style={[styles.compactRating, { color: rankTier.color, fontFamily: TYPO.heading.bold }]}>
          {rating.rating}
        </Text>
      </View>
    );
  }

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}
      onPress={() => router.push('/elo-leaderboard')}
      activeOpacity={0.7}
    >
      <View style={styles.header}>
        <Zap size={16} color={rankTier.color} />
        <Text style={[styles.title, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
          ELO RATING
        </Text>
      </View>

      <View style={styles.ratingRow}>
        <Text style={styles.tierEmoji}>{rankTier.emoji}</Text>
        <View>
          <Text style={[styles.ratingValue, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            {rating.rating}
          </Text>
          <Text style={[styles.tierLabel, { color: rankTier.color, fontFamily: TYPO.body.semibold }]}>
            {rankTier.label}
          </Text>
        </View>
      </View>

      {totalGames > 0 ? (
        <View style={styles.statsRow}>
          <View style={styles.stat}>
            <Text style={[styles.statValue, { color: c.success || '#22c55e', fontFamily: TYPO.body.semibold }]}>
              {rating.wins}W
            </Text>
          </View>
          <View style={styles.stat}>
            <Text style={[styles.statValue, { color: c.error || '#ef4444', fontFamily: TYPO.body.semibold }]}>
              {rating.losses}L
            </Text>
          </View>
          {rating.draws > 0 && (
            <View style={styles.stat}>
              <Text style={[styles.statValue, { color: c.textSecondary, fontFamily: TYPO.body.semibold }]}>
                {rating.draws}D
              </Text>
            </View>
          )}
          <View style={[styles.stat, { flex: 1, alignItems: 'flex-end' }]}>
            <Text style={[styles.statLabel, { color: c.textTertiary }]}>
              {winRate}% WIN RATE
            </Text>
          </View>
        </View>
      ) : (
        <Text style={[styles.emptyText, { color: c.textSecondary }]}>
          No duels yet — challenge someone to get rated!
        </Text>
      )}

      {/* Rating History — Pro gated */}
      <View style={[styles.historySection, { borderTopColor: c.border }]}>
        {isPro ? (
          <TouchableOpacity onPress={() => router.push('/elo-leaderboard')} activeOpacity={0.7}>
            <Text style={[styles.historyLink, { color: c.accent, fontFamily: TYPO.body.semibold }]}>
              View Rating History →
            </Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.lockedRow}>
            <Lock size={12} color={c.textTertiary} />
            <Text style={[styles.lockedText, { color: c.textTertiary }]}>
              Upgrade to Pro for rating analytics
            </Text>
          </View>
        )}
      </View>

      {showCoachInsights && (
        <View style={{ borderTopWidth: 1, borderTopColor: c.border, paddingTop: SPACE.sm, marginTop: SPACE.xs, gap: 6 }}>
          <Text style={{ fontSize: 11, letterSpacing: 1.5, color: c.accent, fontFamily: TYPO.heading.bold }}>COACH INSIGHTS</Text>
          <Text style={{ fontSize: 12, color: c.textSecondary }}>
            Rating deviation: {rating.deviation} · Volatility: {rating.volatility.toFixed(4)}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 16, gap: SPACE.sm },
  header: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  title: { fontSize: 11, letterSpacing: 1.5 },
  ratingRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  tierEmoji: { fontSize: 32 },
  ratingValue: { fontSize: 28 },
  tierLabel: { fontSize: 12, letterSpacing: 1 },
  statsRow: { flexDirection: 'row', gap: SPACE.md, marginTop: 4 },
  stat: {},
  statValue: { fontSize: 13 },
  statLabel: { fontSize: 11, letterSpacing: 0.5 },
  compactContainer: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: RADIUS.xs, paddingVertical: 2, paddingHorizontal: 6 },
  compactEmoji: { fontSize: 12 },
  compactRating: { fontSize: 12 },
  emptyText: { fontSize: 13, fontStyle: 'italic' },
  historySection: { borderTopWidth: 1, paddingTop: 8, marginTop: 4 },
  historyLink: { fontSize: 13 },
  lockedRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  lockedText: { fontSize: 12 },
});
